/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AsyncSelect } from '@atlaskit/select';
import { Controller, Control, DeepMap, FieldError } from 'react-hook-form';
import _ from 'lodash';
import { ErrorMsg } from '../../styles/styles';
import { mapLabelValuePairSameValue } from '../../../pages/sales/utilities';
import CustomizedLabel from './CustomizedLabel';

const SelectWrapper = styled.div`
  margin-top: 10px;
  min-width: 200px;
  &.min-width-120 {
    min-width: 120px;
  }
  &.non-margin-top {
    margin-top: 0px;
  }
  .validation-error > div {
    border-color: #de350b;
  }
`;

type iOption = {
  label: string;
  value: any;
};

const AsyncSelectWithController = ({
  name,
  label,
  placeholder,
  control,
  onChange,
  promiseFn,
  testId = 'async-select-react-hook-form',
  errors,
  className,
  filterFn,
  defaultValue,
  isRequired = false,
  //   isCreatable = true,
  isDisabled = false,
}: {
  name: string;
  label?: string;
  placeholder?: string;
  control: Control<Record<string, any>>;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: (name: string, value: any, config?: Object) => void;
  promiseFn: (keyword: string) => Promise<any>;
  testId?: string;
  isRequired?: boolean;
  errors?: DeepMap<Record<string, any>, FieldError>;
  valid?: boolean;
  className?: string;
  filterFn?: (item: any) => boolean;
  defaultValue?: iOption;
  isCreatable?: boolean;
  isDisabled?: boolean;
}) => {
  const [value, setValue] = useState<iOption>();

  useEffect(() => {
    if (typeof onChange === 'function' && defaultValue?.label) {
      setValue(defaultValue);
      onChange(name, defaultValue.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, JSON.stringify(defaultValue)]);

  const promiseOptions = (
    inputValue: string,
    callback: (options: iOption[]) => void,
  ) => {
    promiseFn(inputValue).then(res => {
      let filtered = res;
      if (filterFn) {
        filtered = res.filter(filterFn);
      }
      const mappedOptions = mapLabelValuePairSameValue(filtered);
      if (mappedOptions) {
        callback(mappedOptions);
      }
    });
  };

  const debouncedOptions = _.debounce(promiseOptions, 700);

  const handleValueChange = (
    selected: iOption,
    // actionMeta: iActionTypes,
  ) => {
    setValue(selected);

    if (selected) {
      onChange(name, selected.value, { shouldValidate: true });
    } else {
      onChange(name, null, { shouldValidate: true });
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: isRequired,
      }}
      render={ctrlProps => (
        <SelectWrapper className={className}>
          <CustomizedLabel
            label={label}
            htmlFor={testId}
            isRequired={isRequired}
          />
          <AsyncSelect
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...ctrlProps}
            isClearable
            loadOptions={debouncedOptions}
            onChange={(selected: any) => handleValueChange(selected)}
            value={value}
            placeholder={placeholder}
            classNamePrefix={testId}
            className={`${testId} async-select-react-hook-form ${
              _.get(errors, name) && 'validation-error'
            }`}
            isDisabled={isDisabled}
          />
          {_.get(errors, name) && <ErrorMsg>must select an element</ErrorMsg>}
        </SelectWrapper>
      )}
    />
  );
};

export default AsyncSelectWithController;
